<app-page-shell [breadcrumb]="breadcrumb" width="wide">
  <header class="mx-auto max-w-3xl px-6 pt-12 pb-14 text-center md:pt-16 md:pb-20">
    <p class="mb-4 eyebrow">{{ 'pages.evidenceExpress.hero.eyebrow' | transloco }}</p>
    <h1 class="hero-title font-display text-4xl font-bold leading-[1.06] text-foreground md:text-[3.25rem]">
      <span>{{ 'pages.evidenceExpress.hero.titlePrefix' | transloco }}</span>
      <span class="hero-title-accent italic font-medium text-primary">{{ 'pages.evidenceExpress.hero.titleAccent' | transloco }}</span>
    </h1>
    <p class="mx-auto mt-6 max-w-2xl text-base leading-relaxed text-muted-foreground md:text-[17px]">
      {{ 'pages.evidenceExpress.hero.description' | transloco }}
    </p>
  </header>

  <!-- Barra de filtros -->
  <div class="rounded-2xl border border-border bg-card p-4 shadow-sm md:p-5">
    <div class="flex flex-col gap-3 md:flex-row md:items-center">
      <div class="flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.14em] text-muted-foreground">
        <svg aria-hidden="true" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="h-3.5 w-3.5 text-primary">
          <polygon points="22 3 2 3 10 12.46 10 19 14 21 14 12.46 22 3" />
        </svg>
        {{ 'pages.evidenceExpress.filter.label' | transloco }}
      </div>

      <app-editorial-select
        [options]="categoriaOptions()"
        [value]="categoria()"
        (valueChange)="categoria.set($event)"
        [ariaLabel]="'pages.evidenceExpress.filter.category' | transloco"
        align="left"
        [block]="true"
      />

      <app-editorial-select
        [options]="temaOptions()"
        [value]="tema()"
        (valueChange)="tema.set($event)"
        [ariaLabel]="'pages.evidenceExpress.filter.theme' | transloco"
        align="left"
        [block]="true"
      />

      <div class="flex flex-1 items-center gap-2 rounded-full border border-border bg-card px-4 py-2.5 transition focus-within:border-primary/60 focus-within:ring-2 focus-within:ring-primary/30">
        <svg aria-hidden="true" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="h-4 w-4 text-muted-foreground">
          <circle cx="11" cy="11" r="8" /><path d="m21 21-4.3-4.3" />
        </svg>
        <input
          #searchInput
          type="text"
          role="searchbox"
          [value]="query()"
          (input)="onSearch(searchInput.value)"
          [attr.placeholder]="'pages.evidenceExpress.filter.searchPlaceholder' | transloco"
          [attr.aria-label]="'pages.evidenceExpress.filter.searchPlaceholder' | transloco"
          class="input-unstyled min-w-0 flex-1 appearance-none border-0 bg-transparent p-0 text-sm text-foreground shadow-none outline-none ring-0 placeholder:text-muted-foreground/70 focus:outline-none focus:ring-0 focus-visible:outline-none focus-visible:ring-0 focus-visible:shadow-none"
        />
      </div>

      <span class="hidden shrink-0 text-xs font-medium text-muted-foreground md:inline tabular-nums">
        {{ total() }} {{ (total() === 1 ? 'pages.evidenceExpress.resultSingular' : 'pages.evidenceExpress.resultPlural') | transloco }}
      </span>
    </div>
  </div>

  <!-- Catálogo -->
  @if (loading()) {
    <div class="mt-12 grid grid-cols-2 gap-5 md:grid-cols-3 lg:grid-cols-4">
      @for (i of [1,2,3,4,5,6,7,8]; track i) {
        <div class="aspect-[3/4] animate-pulse rounded-xl bg-foreground/5"></div>
      }
    </div>
  } @else {
    <div class="mt-12 space-y-16">
      @for (group of groups(); track group.key) {
        <section>
          <header class="mb-5 flex items-end justify-between gap-4 border-b border-border pb-3">
            <div>
              <h2 class="font-display text-xl font-bold text-foreground md:text-2xl">
                {{ group.label }}
              </h2>
              <p class="mt-1 text-sm text-muted-foreground">
                {{ group.items.length }}
                {{ (group.items.length === 1 ? 'pages.evidenceExpress.reportSingular' : 'pages.evidenceExpress.reportPlural') | transloco }}
                @if (group.description) { · {{ group.description }} }
              </p>
            </div>
          </header>

          <div class="grid grid-cols-2 gap-5 md:grid-cols-3 lg:grid-cols-4">
            @for (pub of group.items; track pub.id) {
              <article class="group flex flex-col overflow-hidden rounded-xl border border-border bg-card transition hover:-translate-y-0.5 hover:shadow-md">
                <div class="relative aspect-[3/4] overflow-hidden p-4 text-white {{ group.cover }}">
                  <div class="flex items-center justify-between text-[10px] font-bold uppercase tracking-[0.18em] text-white/85">
                    <span>EvEx</span>
                    <span>{{ monthYear(pub) }}</span>
                  </div>
                  <h3 class="mt-6 line-clamp-5 font-display text-[13px] font-bold leading-snug text-white md:text-sm">
                    {{ pub.titulo }}
                  </h3>
                  <div aria-hidden="true" class="pointer-events-none absolute -bottom-6 -right-3 select-none font-display text-[7rem] font-black leading-none text-white/10">
                    EV
                  </div>
                  <div class="absolute bottom-3 left-4 text-[10px] font-semibold uppercase tracking-[0.16em] text-white/80">
                    ENAP
                  </div>
                </div>

                <div class="flex flex-1 flex-col gap-2 p-4">
                  <span class="inline-flex w-fit rounded-full bg-primary/10 px-2.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-primary">
                    {{ temaChip(pub.tema.nome) }}
                  </span>
                  <p class="line-clamp-2 text-xs leading-snug text-foreground/80">{{ pub.titulo }}</p>

                  <div class="mt-2 flex items-center justify-between border-t border-border pt-2 text-xs">
                    <span class="text-muted-foreground">
                      @if (pub.qtdePaginas) {
                        {{ pub.qtdePaginas }} {{ 'pages.evidenceExpress.pagesSuffix' | transloco }}
                      } @else {
                        {{ 'pages.evidenceExpress.reportLabel' | transloco }}
                      }
                    </span>
                    @if (pub.link_publicacao) {
                      <div class="flex items-center gap-1.5">
                        <a
                          [href]="pub.link_publicacao"
                          target="_blank"
                          rel="noreferrer"
                          [attr.aria-label]="'pages.evidenceExpress.downloadPublication' | transloco"
                          [title]="'pages.evidenceExpress.downloadPublication' | transloco"
                          class="inline-flex items-center gap-1 rounded-full p-1.5 text-muted-foreground transition hover:bg-primary/10 hover:text-primary"
                        >
                          <svg aria-hidden="true" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="h-3.5 w-3.5">
                            <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" /><path d="M7 10l5 5 5-5" /><path d="M12 15V3" />
                          </svg>
                        </a>
                        <a
                          [href]="pub.link_publicacao"
                          target="_blank"
                          rel="noreferrer"
                          [attr.aria-label]="'pages.evidenceExpress.openPublication' | transloco"
                          [title]="'pages.evidenceExpress.openPublication' | transloco"
                          class="inline-flex items-center gap-1 rounded-full p-1.5 text-muted-foreground transition hover:bg-primary/10 hover:text-primary"
                        >
                          <svg aria-hidden="true" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="h-3.5 w-3.5">
                            <path d="M7 7h10v10" /><path d="M7 17 17 7" />
                          </svg>
                        </a>
                      </div>
                    }
                  </div>
                </div>
              </article>
            }
          </div>
        </section>
      }

      @if (total() === 0) {
        <div class="rounded-2xl border border-dashed border-border bg-card p-12 text-center">
          <p class="text-sm text-muted-foreground">
            {{ 'pages.evidenceExpress.empty' | transloco }}
          </p>
        </div>
      }
    </div>
  }

  <app-promo-banner
    eyebrowKey="pages.evidenceExpress.promo.eyebrow"
    titlePrefixKey="pages.evidenceExpress.promo.titlePrefix"
    titleAccentKey="pages.evidenceExpress.promo.titleAccent"
    descriptionKey="pages.evidenceExpress.promo.description"
    ctaLabelKey="pages.evidenceExpress.promo.cta"
    ctaPath="/nuvem-perguntas"
  />
</app-page-shell>
